<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { Notification } from '@/features/notifications/api';
import {
  useMarkAllRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from '@/features/notifications/queries';
import { formatDateTime } from '@/shared/format';
import { placePanel, type PanelPlacement } from '@/features/notifications/panel-placement';
import AppButton from '@/shared/ui/AppButton.vue';

const router = useRouter();

const open = ref(false);
const unread = useUnreadCount();
// The list is only fetched while the panel is open: a badge needs a number, not a page.
const notifications = useNotifications(() => open.value);
const markRead = useMarkNotificationRead();
const markAllRead = useMarkAllRead();

const count = computed(() => unread.data.value?.count ?? 0);

/**
 * The panel is teleported to the body and positioned against the viewport.
 *
 * It used to be an absolutely positioned child of the button, and the rail — which needs
 * `overflow-y-auto` for long navigation — clipped it. A scroll container clips on BOTH
 * axes, so a 20rem panel inside a 15rem rail simply lost its right half. Anchoring it to
 * the other side, which is what an earlier fix did, moved the cut rather than removing it.
 */
const trigger = ref<HTMLElement | null>(null);
const position = ref<PanelPlacement>({ left: 0, top: 0 });

function place(): void {
  const button = trigger.value?.getBoundingClientRect();

  if (button) {
    position.value = placePanel(button, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }
}

function toggle(): void {
  open.value = !open.value;

  if (open.value) {
    place();
  }
}

function close(): void {
  open.value = false;
}

/**
 * Anything that moves the trigger out from under the panel dismisses it. `capture` so a
 * scroll inside the rail counts, not only one on the page.
 *
 * Escape is bound to the window rather than to the panel, because it should close from
 * wherever the focus happens to be — including the trigger the user just clicked.
 */
function onEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    close();
  }
}

onMounted(() => {
  window.addEventListener('resize', close);
  window.addEventListener('scroll', close, true);
  window.addEventListener('keydown', onEscape);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', close);
  window.removeEventListener('scroll', close, true);
  window.removeEventListener('keydown', onEscape);
});

const items = computed<Notification[]>(() => notifications.data.value?.items ?? []);

const label = computed(() =>
  count.value === 0
    ? 'Notificaciones'
    : `Notificaciones, ${count.value} sin leer`,
);

async function openNotification(notification: Notification): Promise<void> {
  close();

  if (notification.readAt === null) {
    await markRead.mutateAsync(notification.id);
  }

  if (notification.entity === 'leave_request') {
    await router.push({ name: 'leave-detail', params: { id: notification.entityId } });
  }
}
</script>

<template>
  <div class="relative">
    <button
      ref="trigger"
      type="button"
      class="relative flex min-h-11 min-w-11 items-center justify-center rounded-lg text-ink-muted hover:bg-surface-muted"
      :aria-label="label"
      :aria-expanded="open"
      aria-haspopup="true"
      @click="toggle"
    >
      <span
        class="text-lg"
        aria-hidden="true"
      >🔔</span>

      <!--
        The count is announced through the button label rather than only drawn, so it
        reaches somebody who cannot see the badge (PQ-007).
      -->
      <span
        v-if="count > 0"
        class="absolute right-1 top-1 min-w-5 rounded-full bg-danger-600 px-1 text-center text-xs font-semibold text-white"
        aria-hidden="true"
      >
        {{ count > 9 ? '9+' : count }}
      </span>
    </button>

    <Teleport to="body">
      <!--
        A real button rather than a bare div: dismissing is an action, so it belongs to
        the keyboard too, and giving it a name is cheaper than explaining why it has none.
      -->
      <button
        v-if="open"
        type="button"
        class="fixed inset-0 z-40 cursor-default"
        aria-label="Cerrar notificaciones"
        @click="close"
      />
      <div
        v-if="open"
        role="dialog"
        tabindex="-1"
        aria-label="Notificaciones"
        class="fixed z-50 w-80 max-w-[calc(100vw-1rem)] rounded-card border border-border bg-surface shadow-lg"
        :style="{
          left: `${position.left}px`,
          top: position.top === undefined ? undefined : `${position.top}px`,
          bottom: position.bottom === undefined ? undefined : `${position.bottom}px`,
        }"
      >
        <div class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="text-sm font-semibold text-ink">
            Notificaciones
          </h2>
          <AppButton
            v-if="count > 0"
            variant="ghost"
            :busy="markAllRead.isPending.value"
            @click="markAllRead.mutate()"
          >
            Marcar todas
          </AppButton>
        </div>

        <p
          v-if="notifications.isPending.value"
          class="px-4 py-6 text-sm text-ink-muted"
        >
          Cargando…
        </p>

        <p
          v-else-if="items.length === 0"
          class="px-4 py-6 text-sm text-ink-muted"
        >
          No hay notificaciones.
        </p>

        <ul
          v-else
          class="max-h-96 divide-y divide-border overflow-y-auto"
        >
          <li
            v-for="notification in items"
            :key="notification.id"
          >
            <button
              type="button"
              class="w-full px-4 py-3 text-left hover:bg-surface-muted"
              :class="notification.readAt === null ? 'bg-brand-50' : ''"
              @click="openNotification(notification)"
            >
              <span class="block text-sm font-medium text-ink">{{ notification.title }}</span>
              <span
                v-if="notification.body"
                class="mt-0.5 block text-xs text-ink-muted"
              >{{ notification.body }}</span>
              <span class="mt-1 block text-xs text-ink-muted">
                {{ formatDateTime(notification.createdAt) }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>
